import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  BarChart3, FileText, CreditCard, Wallet, Receipt, 
  CalendarDays, ListChecks, Key, Users, LogOut, FileCheck, Banknote, Package, DollarSign 
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { api } from '../utils/api';

export default function MobileHome() {
  const navigate = useNavigate();
  const { currentUser, logout, kullanicilar } = useApp();
  const isAdmin = currentUser?.rol === 'admin';

  // Şifre modalleri
  const [showSifreModal, setShowSifreModal] = useState(false);
  const [showAdminSifreModal, setShowAdminSifreModal] = useState(false);
  
  // Kendi şifre state
  const [eskiSifre, setEskiSifre] = useState('');
  const [yeniSifre, setYeniSifre] = useState('');
  const [yeniSifreTekrar, setYeniSifreTekrar] = useState('');
  const [sifreLoading, setSifreLoading] = useState(false);
  
  // Admin şifre state
  const [selectedKullanici, setSelectedKullanici] = useState('');
  const [adminYeniSifre, setAdminYeniSifre] = useState('');
  const [adminSifreLoading, setAdminSifreLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSifreDegistir = async () => {
    if (!eskiSifre || !yeniSifre || !yeniSifreTekrar) {
      toast.error('Tüm alanları doldurun');
      return;
    }
    if (yeniSifre !== yeniSifreTekrar) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    if (yeniSifre.length < 4) {
      toast.error('Şifre en az 4 karakter olmalı');
      return;
    }

    setSifreLoading(true);
    try {
      await api.sifreDegistir(currentUser.id, eskiSifre, yeniSifre);
      toast.success('Şifre başarıyla değiştirildi!');
      setShowSifreModal(false);
      setEskiSifre('');
      setYeniSifre('');
      setYeniSifreTekrar('');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Mevcut şifre hatalı');
      } else {
        toast.error('Şifre değiştirilemedi');
      }
    } finally {
      setSifreLoading(false);
    }
  };

  const handleAdminSifreSifirla = async () => {
    if (!selectedKullanici || !adminYeniSifre) {
      toast.error('Kullanıcı seçin ve yeni şifre girin');
      return;
    }
    if (adminYeniSifre.length < 4) {
      toast.error('Şifre en az 4 karakter olmalı');
      return;
    }

    setAdminSifreLoading(true);
    try {
      const res = await api.adminSifreSifirla(currentUser.id, selectedKullanici, adminYeniSifre);
      toast.success(res.data.message);
      setShowAdminSifreModal(false);
      setSelectedKullanici('');
      setAdminYeniSifre('');
    } catch (error) {
      toast.error('Şifre değiştirilemedi');
    } finally {
      setAdminSifreLoading(false);
    }
  };

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: BarChart3, color: 'bg-blue-500', desc: 'Genel bakış' },
    { path: '/raporlar', label: 'Raporlar', icon: FileText, color: 'bg-green-500', desc: 'Detaylı raporlar' },
    { path: '/faturalar', label: 'Faturalar', icon: FileText, color: 'bg-emerald-500', desc: 'Fatura takibi' },
    { path: '/siparisler', label: 'Siparişler', icon: Package, color: 'bg-amber-500', desc: 'Sipariş takibi' },
    { path: '/gider-gelir', label: 'Günlük Kasa', icon: DollarSign, color: 'bg-rose-500', desc: 'Kasa hareketleri' },
    { path: '/cekler', label: 'Çekler', icon: Receipt, color: 'bg-orange-500', desc: 'Çek takibi' },
    { path: '/diger-odemeler', label: 'Diğer Ödemeler', icon: Wallet, color: 'bg-purple-500', desc: 'Fatura, kira vs.' },
    { path: '/tum-odemeler', label: 'Tüm Ödemeler', icon: ListChecks, color: 'bg-red-500', desc: 'Birleşik görünüm' },
    { path: '/krediler', label: 'Krediler', icon: Banknote, color: 'bg-teal-500', desc: 'Kredi takibi' },
    { path: '/kredi-kartlari', label: 'Kredi Kartları', icon: CreditCard, color: 'bg-pink-500', desc: 'Kart ve ekstre' },
    { path: '/lc', label: 'LC (Akreditif)', icon: FileCheck, color: 'bg-cyan-500', desc: 'USD ödemeler' },
  ];

  if (isAdmin) {
    menuItems.push({ path: '/bankalar', label: 'Bankalar', icon: CalendarDays, color: 'bg-indigo-500', desc: 'Banka yönetimi' });
  }

  return (
    <div className="min-h-screen bg-background p-4 pb-24">
      {/* Şifre Değiştirme Modal */}
      {showSifreModal && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-bold">Şifre Değiştir</h2>
              <button onClick={() => setShowSifreModal(false)} className="text-2xl">&times;</button>
            </div>
            <div className="p-4 space-y-4">
              <Input type="password" value={eskiSifre} onChange={(e) => setEskiSifre(e.target.value)} placeholder="Mevcut şifre" />
              <Input type="password" value={yeniSifre} onChange={(e) => setYeniSifre(e.target.value)} placeholder="Yeni şifre" />
              <Input type="password" value={yeniSifreTekrar} onChange={(e) => setYeniSifreTekrar(e.target.value)} placeholder="Yeni şifre (tekrar)" />
            </div>
            <div className="flex gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowSifreModal(false)} className="flex-1">İptal</Button>
              <Button onClick={handleSifreDegistir} disabled={sifreLoading} className="flex-1 bg-primary">
                {sifreLoading ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Şifre Modal */}
      {showAdminSifreModal && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-bold">Kullanıcı Şifre Yönetimi</h2>
              <button onClick={() => setShowAdminSifreModal(false)} className="text-2xl">&times;</button>
            </div>
            <div className="p-4 space-y-4">
              <Select value={selectedKullanici} onValueChange={setSelectedKullanici}>
                <SelectTrigger><SelectValue placeholder="Kullanıcı seçin" /></SelectTrigger>
                <SelectContent>
                  {kullanicilar?.filter(k => k.id !== currentUser?.id).map((k) => (
                    <SelectItem key={k.id} value={k.id}>{k.ad}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="password" value={adminYeniSifre} onChange={(e) => setAdminYeniSifre(e.target.value)} placeholder="Yeni şifre" />
            </div>
            <div className="flex gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowAdminSifreModal(false)} className="flex-1">İptal</Button>
              <Button onClick={handleAdminSifreSifirla} disabled={adminSifreLoading} className="flex-1 bg-primary">
                {adminSifreLoading ? 'Kaydediliyor...' : 'Değiştir'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <img 
            src="https://customer-assets.emergentagent.com/job_branch-eod-system/artifacts/oajdv090_IMG_1236.png" 
            alt="MİBA GRUP" 
            className="h-10 w-auto"
          />
        </div>
        <div className="text-right">
          <p className="font-medium text-sm">{currentUser?.ad}</p>
          <p className="text-xs text-textSecondary">{isAdmin ? 'Yönetici' : 'Personel'}</p>
        </div>
      </div>

      {/* Ana Menü Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="bg-surface border border-border rounded-xl p-4 text-left hover:shadow-md transition-all active:scale-95"
            >
              <div className={`${item.color} w-12 h-12 rounded-lg flex items-center justify-center mb-3`}>
                <Icon className="h-6 w-6 text-white" />
              </div>
              <p className="font-medium text-sm">{item.label}</p>
              <p className="text-xs text-textSecondary">{item.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Ayarlar Bölümü */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
        <h3 className="font-medium text-sm text-textSecondary mb-2">Ayarlar</h3>
        
        <button
          onClick={() => setShowSifreModal(true)}
          className="w-full flex items-center gap-3 p-3 bg-surfaceSubtle rounded-lg hover:bg-gray-100 transition-colors"
        >
          <Key className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium">Şifremi Değiştir</span>
        </button>

        {isAdmin && (
          <button
            onClick={() => setShowAdminSifreModal(true)}
            className="w-full flex items-center gap-3 p-3 bg-surfaceSubtle rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Users className="h-5 w-5 text-amber-600" />
            <span className="text-sm font-medium">Kullanıcı Şifreleri</span>
          </button>
        )}

        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
        >
          <LogOut className="h-5 w-5 text-red-500" />
          <span className="text-sm font-medium text-red-600">Çıkış Yap</span>
        </button>
      </div>
    </div>
  );
}
